import mixpanel, { type Callback, type Dict, type RequestOptions } from 'mixpanel-browser';

import { coerceMixpanelShopMetrics } from '@/helpers/analytics/mixpanel-metrics';
import { getAppContext } from '@/helpers/get-app-context';

const currentVersion = import.meta.env.VITE_APP_VERSION as string | undefined;

const VALID_MIXPANEL_PLAN_IDS = new Set([0, 1, 2, 6]);
const MIXPANEL_PLAN_NAMES: Record<number, string> = {
  0: 'Free',
  1: 'Pro',
  2: 'Basic',
  6: 'Expert',
};

function normalizeMixpanelPlanSuperProps(props: Dict | undefined, fallbackPlanId?: unknown): Dict {
  const raw = props?.['Plan ID'] ?? fallbackPlanId;
  const num = typeof raw === 'number' ? raw : Number(raw);
  const planId =
    Number.isFinite(num) && VALID_MIXPANEL_PLAN_IDS.has(num) ? num : 0;
  return {
    'Plan ID': planId,
    'Plan Name': MIXPANEL_PLAN_NAMES[planId] ?? 'Free',
  };
}

let mixpanelInitialized = false;

export function initialize() {
  // Reset credentials must never enter analytics or session recordings.
  if (/^\/reset-password(?:\/|$)/.test(window.location.pathname)) {
    return;
  }

  mixpanel.init(import.meta.env.VITE_APP_MIXPANEL_PROJECT_TOKEN, {
    batch_requests: false,
    cross_subdomain_cookie: true,
    debug: false,
    persistence: 'cookie',
    secure_cookie: true,
    track_pageview: false,
  });

  const user = window.__DATA__?.user;

  if (user) {
    mixpanel.identify(String(user.member_id));
    const serverProps = { ...(getAppContext().MIXPANEL_PROPERTIES ?? {}) };
    const planProps = normalizeMixpanelPlanSuperProps(serverProps, user.plan_id);
    const merged = coerceMixpanelShopMetrics({ ...serverProps, ...planProps });
    mixpanel.people.set(merged);
    mixpanel.register(merged);
  } else {
    mixpanel.register(normalizeMixpanelPlanSuperProps(undefined));
  }

  mixpanelInitialized = true;
}

export function startSessionRecording() {
  if (mixpanelInitialized) {
    mixpanel.set_config({
      record_sessions_percent: 50,
      record_mask_text_selector: "input[type=password]",
    });
    mixpanel.start_session_recording();
  }
}

export function stopSessionRecording() {
  if (mixpanelInitialized) {
    mixpanel.stop_session_recording();
  }
}

export function reset() {
  if (mixpanelInitialized) {
    mixpanel.reset();
  }
}

export function track(
  eventName: string,
  properties?: Dict,
  optionsOrCallback?: RequestOptions | Callback,
  callback?: Callback,
) {
  if (!mixpanelInitialized) {
    return;
  }

  mixpanel.track(eventName, { appVersion: currentVersion, ...properties }, optionsOrCallback, callback);
}

export function appendUserProperties(appendProperties: Dict) {
  if (!mixpanelInitialized) {
    return;
  }

  if (appendProperties && Object.keys(appendProperties).length > 0) {
    mixpanel.people.append(appendProperties);
  }
}

export function updateSuperProperties(properties: Dict) {
  if (!mixpanelInitialized) {
    return;
  }

  if (properties && Object.keys(properties).length > 0) {
    const normalized = coerceMixpanelShopMetrics(properties);
    mixpanel.register(normalized);
    mixpanel.people.set(normalized);
  }
}

/** Call after `/user` (or similar) updates plan so super properties match the API. */
export function syncMixpanelPlanFromUser(planId?: unknown) {
  if (!mixpanelInitialized) {
    return;
  }

  const fallback = planId ?? window.__DATA__?.user?.plan_id;
  const planProps = normalizeMixpanelPlanSuperProps(undefined, fallback);
  mixpanel.register(planProps);
  mixpanel.people.set(planProps);
}
